import type { Survey, SurveyOverallScores } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import {
    createFileRoute,
    type ErrorComponentProps,
    Link,
    redirect,
    retainSearchParams,
    SearchParamError,
    stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { BarChart3Icon, Sparkles, Star, Trophy } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { NotFound, NotFoundError } from '@/components/NotFound';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { SelectSurvey } from '@/components/surveys/common/SelectSurvey';
import {
    DiAverageChartPerAttributes,
    DiAverageChartPerAttributesSkeleton,
} from '@/components/surveys/summaries/DiAverageChartPerAttributes';
import {
    DiAverageChartPerYear,
    DiAverageChartPerYearSkeleton,
} from '@/components/surveys/summaries/DiAverageChartPerYear';
import { GlobalScore, GlobalScoreSkeleton } from '@/components/surveys/summaries/GlobalScore';
import { ImprovementArea, ImprovementAreaSkeleton } from '@/components/surveys/summaries/ImprovementArea';
import { ResponderCount, ResponderCountSkeleton } from '@/components/surveys/summaries/ResponderCount';
import { TopCategory, TopCategorySkeleton } from '@/components/surveys/summaries/TopCategory';
import { fetchSurveys } from '@/queries/surveys/client';
import { pageBreakBefore, pageBreakBeforeInLandscape } from '@/styles/print.css';
import { combineQueryErrors, formatDateToLabel } from '@/utils';
import {
    useOverallScores,
    useScoresByAttributes,
    useScoresByCategory,
    useScoresByDepartment,
    useScoresByQuestion,
    useSurveyAttributes,
} from '../-hooks';
import { resolveScoresChain } from '../-utils';
import { RankingsByDepartment, RankingsByDepartmentSkeleton } from './-fragments/RankingsByDepartment';

const DefaultRouteQueryParams = {
    surveyId: '',
};

const RouteQueryParams = z.object({
    surveyId: uuidSchema.default(DefaultRouteQueryParams.surveyId),
});

export const Route = createFileRoute('/_client/_auth/surveys/summaries/')({
    component: SurveySummariesRoute,
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [retainSearchParams(['surveyId']), stripSearchParams(DefaultRouteQueryParams)],
    },
    loaderDeps: ({ search }) => ({ surveyId: search.surveyId }),
    loader: async ({ deps }) => {
        const { surveys } = await fetchSurveys();

        if (deps.surveyId === '' && surveys.length > 0) {
            // Canonicalizes the URL to the survey actually being shown, so a shared/reloaded
            // link keeps pointing at it rather than silently drifting to whatever's newest.
            throw redirect({ to: '/surveys/summaries', search: { surveyId: surveys[0].surveyId }, replace: true });
        }

        if (!surveys.some((survey) => survey.surveyId === deps.surveyId)) {
            throw new NotFoundError();
        }

        return { surveys };
    },
    pendingComponent: SurveySummariesRouteSkeleton,
    errorComponent: SurveySummariesRouteError,
});

function SurveySummariesRoute() {
    const { surveys } = Route.useLoaderData();
    const { surveyId } = Route.useSearch();
    const navigate = Route.useNavigate();

    // The loader redirects an empty `surveyId` to the newest survey's id, and rejects a
    // non-matching one, so this is always a valid index once there's at least one survey.
    const selectedSurveyIdx = surveys.findIndex((s) => s.surveyId === surveyId);
    const surveysToLoad = surveys.slice(selectedSurveyIdx);
    const overallScoresQuery = useOverallScores(surveysToLoad.map((s) => s.surveyId));

    if (surveys.length === 0) {
        // TODO: Visual design for empty state
        return <div>サーベイがありません。</div>;
    }

    const selectedSurvey = surveys[selectedSurveyIdx];

    return (
        <Page.Root>
            <Page.Header title="結果分析 - 全社" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    <SelectSurvey
                        surveys={surveys.map((survey) => ({
                            id: survey.surveyId,
                            name: formatDateToLabel(survey.surveyDate),
                        }))}
                        selectedSurveyId={selectedSurvey.surveyId}
                        onSurveyChange={(id) => navigate({ search: { surveyId: id } })}
                    />

                    <CreatePdfButton />
                </Flex>

                {overallScoresQuery.isPending && <SurveySummariesSkeleton />}

                <ErrorOverlay
                    active={overallScoresQuery.isError}
                    onRetry={overallScoresQuery.refetch}
                    message="全社サマリーの取得に失敗しました。"
                />

                {overallScoresQuery.data && selectedSurvey && (
                    <SurveySummaries
                        scores={resolveScoresChain(surveysToLoad, overallScoresQuery.data.scores)}
                        selectedSurvey={selectedSurvey}
                        isError={overallScoresQuery.isError}
                        onRetry={overallScoresQuery.refetch}
                    />
                )}
            </Page.Content>
        </Page.Root>
    );
}

function SurveySummariesRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="結果分析 - 全社" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    {/* Absolute widths: these sit in shrink-to-fit flex items, so a
                        percentage would have no definite width to resolve against. */}
                    <Flex direction="column" gap="2">
                        <Skeleton.Line width={96} height="sm" />
                        <Skeleton.Box width={300} height={42} />
                    </Flex>

                    <Skeleton.Box width={168} height={36} />
                </Flex>

                <SurveySummariesSkeleton />
            </Page.Content>
        </Page.Root>
    );
}

function SurveySummariesRouteError({ error, reset }: ErrorComponentProps) {
    if (error instanceof SearchParamError) {
        return <InvalidRequest kind="client" />;
    }

    if (error instanceof NotFoundError) {
        return <NotFound kind="client" />;
    }

    return (
        <RouteError
            kind="client"
            title="結果分析 - 全社"
            onRetry={reset}
            message="結果分析 - 全社の取得に失敗しました。"
        />
    );
}
type SurveySummariesProps = {
    scores: SurveyOverallScores[];
    selectedSurvey: Survey;
    /**
     * `SurveySummaries` only renders once `overallScoresQuery.data` exists, but that
     * doesn't make this permanently `false` — on a failed background refetch (window
     * refocus, reconnect, a stale remount), React Query flips `isError` to `true` while
     * leaving `data` at its last successful value, so this can still fire after the
     * initial load.
     */
    isError: boolean;
    onRetry: () => void;
};

function SurveySummaries({ scores, selectedSurvey, isError, onRetry }: SurveySummariesProps) {
    const [selectedSettingId, setSelectedSettingId] = useState<string | null>(null);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;
    const previousSurveyScores = previousSurveyId ? scores.find((s) => s.surveyId === previousSurveyId) : undefined;

    const surveyAttributesQuery = useSurveyAttributes(selectedSurveyId);

    const scoresByAttributeQuery = useScoresByAttributes(selectedSurveyId, selectedSettingId, previousSurveyId);
    const scoresByAttributeError = combineQueryErrors(surveyAttributesQuery, scoresByAttributeQuery);
    const currentScoresByAttribute = scoresByAttributeQuery.data?.surveys.find((s) => s.surveyId === selectedSurveyId);
    const previousScoresByAttribute = scoresByAttributeQuery.data?.surveys.find((s) => s.surveyId === previousSurveyId);

    const scoresByCategoryQuery = useScoresByCategory([selectedSurveyId]);
    const currentSurveyScoresByCategory = scoresByCategoryQuery.data?.scores.at(0);

    const scoresPerQuestionQuery = useScoresByQuestion([selectedSurveyId]);
    const currentSurveyScoresByQuestions = scoresPerQuestionQuery.data?.scores.at(0);

    const improvementAreaError = combineQueryErrors(scoresByCategoryQuery, scoresPerQuestionQuery);

    const {
        scoresByDepartment,
        isError: isScoresByDepartmentError,
        retry: retryScoresByDepartment,
    } = useScoresByDepartment(selectedSurveyId);
    const currentSurveyScoresByDepartment = scoresByDepartment.data?.surveys.at(0)?.scores ?? null;

    const { highestCategory, mostImprovedCategory } = currentSurveyScores;

    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <GlobalScore
                    icon={Star}
                    title="DI 平均"
                    scores={{ value: currentSurveyScores.di, previousValue: previousSurveyScores?.di ?? null }}
                    isError={isError}
                    onRetry={onRetry}
                />
                <GlobalScore
                    icon={BarChart3Icon}
                    title="偏差値"
                    scores={{
                        value: currentSurveyScores.deviation,
                        previousValue: previousSurveyScores?.deviation ?? null,
                    }}
                    isError={isError}
                    onRetry={onRetry}
                />
                <ResponderCount
                    responders={currentSurveyScores.responseRate.responders}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            {(highestCategory || mostImprovedCategory) && (
                <Flex gap="4">
                    {highestCategory && (
                        <TopCategory
                            icon={Trophy}
                            title="最高評価カテゴリー"
                            category={highestCategory}
                            isError={isError}
                            onRetry={onRetry}
                        />
                    )}
                    {mostImprovedCategory && (
                        <TopCategory
                            icon={Sparkles}
                            title="一番改善できたカテゴリー"
                            category={mostImprovedCategory}
                            isError={isError}
                            onRetry={onRetry}
                        />
                    )}
                </Flex>
            )}

            <div className={pageBreakBeforeInLandscape}>
                <RankingsByDepartment
                    attributes={surveyAttributesQuery.data?.attributes ?? null}
                    scores={currentSurveyScoresByDepartment}
                    isError={isScoresByDepartmentError}
                    onRetry={retryScoresByDepartment}
                />
            </div>

            {/* Print layout: scores and top categories, then the charts, then the improvement area. */}
            <Flex gap="4" className={pageBreakBefore}>
                <DiAverageChartPerYear scores={scores} isError={isError} onRetry={onRetry} />

                <DiAverageChartPerAttributes
                    scores={currentScoresByAttribute?.scores ?? null}
                    previousScores={previousScoresByAttribute?.scores ?? null}
                    attributes={surveyAttributesQuery.data?.attributes ?? null}
                    onSettingChange={setSelectedSettingId}
                    isError={scoresByAttributeError.isError}
                    onRetry={scoresByAttributeError.retry}
                />
            </Flex>

            <div className={pageBreakBeforeInLandscape}>
                <ImprovementArea
                    scoresByCategory={currentSurveyScoresByCategory?.categories ?? null}
                    scoresByQuestion={currentSurveyScoresByQuestions?.questions ?? null}
                    isError={improvementAreaError.isError}
                    onRetry={improvementAreaError.retry}
                    renderQuestionLink={(questionId, pill) => (
                        <Link to="/surveys/questions" search={{ surveyId: selectedSurveyId, questionId }}>
                            {pill}
                        </Link>
                    )}
                />
            </div>
        </Flex>
    );
}

function SurveySummariesSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <GlobalScoreSkeleton />
                <GlobalScoreSkeleton />
                <ResponderCountSkeleton />
            </Flex>

            <Flex gap="4">
                <TopCategorySkeleton />
                <TopCategorySkeleton />
            </Flex>

            <RankingsByDepartmentSkeleton />

            <Flex gap="4">
                <DiAverageChartPerYearSkeleton />
                <DiAverageChartPerAttributesSkeleton />
            </Flex>

            <ImprovementAreaSkeleton />
        </Flex>
    );
}
